import React, { useEffect, useRef, useMemo } from 'react';
import { useBox, useSphere, useHingeConstraint } from '@react-three/cannon';
import { useFrame } from '@react-three/fiber';
import { Edges } from '@react-three/drei';
import * as THREE from 'three';

// Constants duplicated to keep component self-contained
const FLIPPER_LENGTH = 4.2;
const FLIPPER_WIDTH = 0.8;
const FLIPPER_HEIGHT = 1.0;
const COLOR_NEON_AMBER = '#ffaa00';

const NeonShape = ({ color, ...props }) => (
    <mesh {...props}>
        <meshStandardMaterial color="#000000" roughness={0.1} metalness={0.8} />
        <Edges color={color} threshold={15} scale={1.0} />
        {props.children}
    </mesh>
);

const Flipper = ({ position, type, onFlip }) => {
    const isLeft = type === 'LEFT';
    const length = FLIPPER_LENGTH;
    const width = FLIPPER_WIDTH;
    const height = FLIPPER_HEIGHT;

    const shape = useMemo(() => {
        const s = new THREE.Shape();
        const tipRadius = width * 0.4;
        const baseRadius = width * 0.8;
        const len = length;
        s.absarc(0, 0, baseRadius, Math.PI / 2, -Math.PI / 2, true);
        const angle = Math.asin((baseRadius - tipRadius) / len);
        s.lineTo(len, -tipRadius);
        s.absarc(len, 0, tipRadius, -Math.PI / 2, Math.PI / 2, true);
        s.lineTo(0, baseRadius);
        return s;
    }, [length, width]);

    const offset = length / 2;
    const bodyPos = [
        position[0] + (isLeft ? offset : -offset),
        position[1],
        position[2]
    ];

    // Anchor (Static Pivot)
    const [anchorRef] = useSphere(() => ({
        position,
        args: [0.1],
        type: 'Static',
        collisionFilterGroup: 0
    }));

    // Flipper Body (Dynamic)
    const [ref, api] = useBox(() => ({
        mass: 100, // Massive for rigid interaction
        args: [length, height, width],
        position: bodyPos,
        material: { friction: 0.1, restitution: 0.5 },
        collisionFilterGroup: 2
    }));

    // Hinge Constraint (Passive limits, Motor disabled)
    useHingeConstraint(anchorRef, ref, {
        pivotA: [0, 0, 0],
        pivotB: [isLeft ? -offset : offset, 0, 0],
        axisA: [0, 1, 0],
        axisB: [0, 1, 0],
        lowerLimit: isLeft ? -Math.PI / 6 : -Math.PI / 6,
        upperLimit: isLeft ? Math.PI / 6 : Math.PI / 6,
        enableMotor: false, // We use Software Servo (PD Controller)
    });

    const isFlipping = useRef(false);
    const rotation = useRef([0, 0, 0, 1]);
    const angularVelocity = useRef([0, 0, 0]);

    // Reusable temp objects to avoid GC
    const tempQuat = useMemo(() => new THREE.Quaternion(), []);
    const tempEuler = useMemo(() => new THREE.Euler(), []);

    useEffect(() => {
        const unsubRot = api.quaternion.subscribe((v) => (rotation.current = v));
        const unsubAngVel = api.angularVelocity.subscribe((v) => (angularVelocity.current = v));
        return () => { unsubRot(); unsubAngVel(); };
    }, [api]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((isLeft && e.code === 'ArrowLeft') || (!isLeft && e.code === 'ArrowRight')) {
                if (!isFlipping.current) onFlip();
                isFlipping.current = true;
            }
        };
        const handleKeyUp = (e) => {
            if ((isLeft && e.code === 'ArrowLeft') || (!isLeft && e.code === 'ArrowRight')) {
                isFlipping.current = false;
            }
        };
        const handleTouchStart = (e) => {
            const touchX = e.touches[0].clientX;
            const halfWidth = window.innerWidth / 2;
            if ((isLeft && touchX < halfWidth) || (!isLeft && touchX >= halfWidth)) {
                if (!isFlipping.current) onFlip();
                isFlipping.current = true;
            }
        };
        const handleTouchEnd = () => {
            isFlipping.current = false;
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('touchstart', handleTouchStart);
        window.addEventListener('touchend', handleTouchEnd);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isLeft, onFlip]);

    useFrame(() => {
        // --- PD CONTROLLER (Software Servo) ---
        // Torque = Kp * error - Kd * velocity

        // 1. Get Current Angle
        tempQuat.set(...rotation.current);
        tempEuler.setFromQuaternion(tempQuat);
        const currentAngle = tempEuler.y;

        // 2. Determine Target Angle
        // Left: Rest = -30deg (-0.52), Hit = +30deg (+0.52)
        // Right: Rest = +30deg (+0.52), Hit = -30deg (-0.52)
        const targetUp = isLeft ? Math.PI / 6 : -Math.PI / 6;
        const targetDown = isLeft ? -Math.PI / 6 : Math.PI / 6;
        const target = isFlipping.current ? targetUp : targetDown;

        // 3. Calculate Error
        const error = target - currentAngle;

        // 4. PD Constants (Tuned for Mass 100)
        const Kp = 300000; // Stiffness (Very High)
        const Kd = 2000;   // Damping (Prevent oscillation)

        // 5. Calculate Torque
        const currentVel = angularVelocity.current[1]; // Y-axis velocity
        const torque = Kp * error - Kd * currentVel;

        // 6. Apply
        api.applyTorque([0, torque, 0]);
    });

    return (
        <group ref={ref}>
            <NeonShape color={COLOR_NEON_AMBER} rotation={[Math.PI / 2, 0, isLeft ? 0 : Math.PI]} position={[isLeft ? -offset : offset, 0, 0]}>
                <extrudeGeometry args={[shape, { depth: height, bevelEnabled: false }]} />
            </NeonShape>
        </group>
    );
};

export default Flipper;
